// Helpers for linking checklist recipe resources onto a workflow step TASK.

import { collectChecklistResourceNodeKeys } from 'utilities/ChecklistItemResources';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import { getNodesByKeys } from 'services/nodes/NodeReadService';
import { upsertEdge, upsertEdges } from 'services/nodes/NodeEdgeService';

/**
 * Build edge payloads that relate checklist resource nodes under a step TASK.
 *
 * @param {object} options
 * @param {object|null} options.stepNode
 * @param {unknown} options.recipeItems
 * @param {Map<string, object>|null} [options.nodesByKey] - Optional preloaded resource nodes.
 * @returns {Array<object>}
 */
export function collectWorkflowStepChecklistResourceLinks({
  stepNode = null,
  recipeItems = [],
  nodesByKey = null,
} = {}) {
  const parentKey = String(stepNode?.key || '').trim();
  const baseKey = String(stepNode?.baseKey || '').trim() || null;
  const resourceKeys = collectChecklistResourceNodeKeys(recipeItems);

  if (!parentKey || !resourceKeys.length) return [];

  return resourceKeys.map((resourceKey) => {
    const childNode = (
      nodesByKey instanceof Map && nodesByKey.has(resourceKey)
        ? nodesByKey.get(resourceKey)
        : {
          key: resourceKey,
          type: null,
          baseKey,
        }
    );

    return {
      nodeKeyA: parentKey,
      nodeKeyB: resourceKey,
      ...buildHierarchyEdgeOptions({
        parentNode: stepNode,
        childNode,
        baseKey: baseKey || childNode?.baseKey || null,
      }),
      skipActivity: true,
    };
  });
}

/**
 * Ensure checklist resource nodes are related children of the step TASK.
 *
 * @param {object} options
 * @param {object|null} options.stepNode
 * @param {unknown} options.recipeItems
 * @returns {Promise<void>}
 */
export async function ensureWorkflowStepChecklistResourcesLinked({
  stepNode = null,
  recipeItems = [],
} = {}) {
  const resourceKeys = collectChecklistResourceNodeKeys(recipeItems);
  if (!resourceKeys.length || !String(stepNode?.key || '').trim()) return;

  const baseKey = String(stepNode?.baseKey || '').trim() || null;
  let resourceNodes = [];
  try {
    resourceNodes = await getNodesByKeys(resourceKeys);
  } catch (error) {
    console.warn('[workflowStepChecklistResourceUtils] Failed to load resources:', error);
    resourceNodes = [];
  }

  const nodesByKey = new Map(
    (Array.isArray(resourceNodes) ? resourceNodes : [])
      .filter((node) => node?.key)
      .map((node) => [String(node.key).trim(), node]),
  );

  const links = collectWorkflowStepChecklistResourceLinks({
    nodesByKey,
    recipeItems,
    stepNode,
  });
  if (!links.length) return;

  try {
    await upsertEdges(links, {
      baseKey,
      skipActivity: true,
    });
  } catch (error) {
    console.warn('[workflowStepChecklistResourceUtils] Batch link failed, falling back:', error);
    await Promise.all(links.map(async (link) => {
      try {
        await upsertEdge(link.nodeKeyA, link.nodeKeyB, {
          baseKey: link.baseKey || baseKey,
          parentKey: link.parentKey || null,
          skipActivity: true,
        });
      } catch (linkError) {
        console.warn(
          '[workflowStepChecklistResourceUtils] Failed to link resource:',
          link.nodeKeyB,
          linkError,
        );
      }
    }));
  }
}
